import Guid from "common/values/guid/guid";
import moment, { Moment } from "moment";
import Action from "notifications/values/action/action";
import NotificationMessage from "notifications/values/notification-message/notification-message";
import NotificationRecipient from "notifications/values/notification-recipient/notification-recipient";
import NotificationTopic from "notifications/values/notification-topic/notification-topic";


export default class Notification {
  
  id: Guid;
  recipients: NotificationRecipient[];
  senderUserId?: Guid;
  message?: NotificationMessage;
  topic?: NotificationTopic | null;
  actionRequired?: Action;
  createdDate?: Moment;
  isCanceled?: boolean;
  senderName?: string;
  recipientName?: string;

  constructor(
    id: Guid,
    recipients?: NotificationRecipient[],
    senderId?: Guid,
    message?: NotificationMessage,
    topic?: NotificationTopic | null,
    actionRequired?: Action,
    createdDate?: Moment,
    isCanceled?: boolean,
    senderName?: string,
    recipientName?: string
  ) {
    this.id = id;
    this.recipients = recipients ?? [];
    this.senderUserId = senderId;
    this.message = message;
    this.topic = topic;
    this.actionRequired = actionRequired;
    this.createdDate = createdDate;
    this.isCanceled = isCanceled;
    this.senderName = senderName;
    this.recipientName = recipientName;
  }

  seenAt(userId?: Guid): Moment | null {
    if (!userId) {
      console.warn("User ID is undefined. Cannot get seen date.");
      return null;
    }
    const recipient = this.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.seenAt : null;
  }

  isSeen(userId?: Guid): boolean {
    if (!userId){
      console.warn("User ID is undefined. Cannot check if notification is seen.");
      return false;
    }
    const recipient = this.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.isSeen : false;
  }
  
  markSeen(userId?: Guid): void {
    if (!userId) {
      console.warn("User ID is undefined. Cannot mark notification as seen.");
      return;
    }
    const recipient = this.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    if (recipient) {
      const updatedRecipient = new NotificationRecipient(
        recipient.userId,
        moment()
      );
      this.recipients = this.recipients.map((r) =>
        r.userId.isEqualTo(userId) ? updatedRecipient : r
      );
    } else {
      console.warn(`User with ID ${userId.value} is not a recipient of this notification.`);
    }
  }

  hasRecipient(id: Guid | undefined) {
    if (!id) {
      console.warn("User ID is undefined. Cannot check if notification has recipient.");
      return false;
    }
    return this.recipients.some((recipient) => recipient.userId.isEqualTo(id));
  }

  /** Static version for type safety */
  static seenAt(notification?: Notification, userId?: Guid): Moment | null {
    if (!(notification instanceof Notification)) {
      return null;
    }
    if (!userId) {
      console.warn("User ID is undefined. Cannot get seen date.");
      return null;
    }
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.seenAt : null;
  }

  /** Static version for type safety */
  static isSeen(notification?: Notification, userId?: Guid): boolean {
    if (!(notification instanceof Notification)) {
      return false;
    }
    if (!userId){
      console.warn("User ID is undefined. Cannot check if notification is seen.");
      return false;
    }
    const recipient = notification.recipients.find((recipient) => recipient.userId.isEqualTo(userId));
    return recipient ? recipient.isSeen : false;
  }

  /** Static version for type safety */
  static hasRecipient(notification?: Notification, userId?: Guid) : boolean {
    if (!(notification instanceof Notification)) {
      return false;
    }
    if (!userId) {
      console.warn("User ID is undefined. Cannot check if notification has recipient.");
      return false;
    }
    return notification.recipients.some((recipient) => recipient.userId.isEqualTo(userId));
  }
}
